import * as React from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import storageUtils from 'bundles/program-common/utils/storageUtils';
import {
  ALTERNATIVE_EMAIL_CACHE_DAYS,
  ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS,
  ALTERNATIVE_EMAIL_NEVER_EXPIRY,
  STORAGE_KEY,
} from 'bundles/program-home/constants/forcedSSORecoveryEmail';
import useCheckSignificantCourseProgress from 'bundles/program-home/queryHooks/useSignificantCourseProgress';

const useShouldShowAlternativeEmailModal = (
  userId: number,
  userEmails: UserEmail[],
  programId: string,
  queryParams: InjectedRouter['location']['query']
) => {
  const [shouldShowAlternativeEmailModal, setShouldShowAlternativeEmailModal] = React.useState(false);
  const hasSignificantCourseProgress = useCheckSignificantCourseProgress(programId);
  const openModalQueryParam = queryParams?.openRecoveryModal;

  React.useEffect(() => {
    if (userEmails.length > 1) {
      setShouldShowAlternativeEmailModal(false);
      storageUtils.set(userId, programId, STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    const shouldOpenAfterStoragePersistedDuration =
      storageUtils.get(userId, programId, STORAGE_KEY, Infinity) < Date.now();
    const shouldOpenViaQueryParams = openModalQueryParam === 'true';

    if ((shouldOpenAfterStoragePersistedDuration && hasSignificantCourseProgress) || shouldOpenViaQueryParams) {
      setShouldShowAlternativeEmailModal(true);
    }
  }, [userId, userEmails, programId, openModalQueryParam, hasSignificantCourseProgress]);

  const setModalState = (isOpen: boolean) => {
    if (!isOpen) {
      let dismissedForDuration = ALTERNATIVE_EMAIL_CACHE_DAYS;
      if (hasSignificantCourseProgress) {
        dismissedForDuration = ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS;
      }
      storageUtils.set(userId, programId, STORAGE_KEY, Date.now() + dismissedForDuration);
    }
    setShouldShowAlternativeEmailModal(isOpen);
  };

  return { shouldShowAlternativeEmailModal, setModalState };
};

export default useShouldShowAlternativeEmailModal;
